// src/features/events/BackupDialogs.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Stack,
  IconButton,
  Paper,
} from "@mui/material";
import {
  Backup as BackupIcon,
  DeleteSweep as DeleteSweepIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  //   Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { formatDistanceToNow } from "date-fns";
import {
  selectBackupSettings,
  selectBackupStatus,
  selectBackupRunning,
  runBackup,
  runBackupAndDelete,
  updateSystemSettings,
  fetchBackupStatus,
  fetchSystemSettings,
} from "./backupSlice";
import { getEvents, resetEvents } from "./eventsSlice";
// import { selectFilters } from "../filters/filterSlice";

// ═══════════════════════════════════════════════════════════
// BACKUP ONLY DIALOG
// ═══════════════════════════════════════════════════════════

export function BackupConfirmDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const backupRunning = useSelector(selectBackupRunning);
  const status = useSelector(selectBackupStatus);

  const handleStart = async () => {
    await dispatch(runBackup());
    // Refresh events to show ✅ ticks
    dispatch(resetEvents());
    dispatch(getEvents(1, true, null, 10));
    dispatch(fetchBackupStatus());
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
      >
        <BackupIcon color="primary" />
        Backup Media Folder
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will upload your <strong>media folder</strong> to Google Drive
          at:
          <Box
            component="code"
            sx={{
              display: "block",
              mt: 1,
              p: 1,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              fontSize: "0.85rem",
            }}
          >
            Camera Trap Backup/Media_Backup_Oct2026
          </Box>
        </DialogContentText>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>No events will be deleted.</strong> Backup only.
        </Alert>

        {status && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Untagged events on server:{" "}
              <strong>{status.untagged_count}</strong>
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              Total events: <strong>{status.total_events}</strong>
            </Typography>
          </Box>
        )}

        {backupRunning && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              Backing up... This may take several minutes.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={backupRunning}>
          Cancel
        </Button>
        <Button
          onClick={handleStart}
          variant="contained"
          disabled={backupRunning}
          startIcon={<BackupIcon />}
        >
          {backupRunning ? "Backing up..." : "Start Backup"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// BACKUP + DELETE DIALOG (2-step confirmation)
// ═══════════════════════════════════════════════════════════

export function BackupDeleteConfirmDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const backupRunning = useSelector(selectBackupRunning);
  const status = useSelector(selectBackupStatus);
  const settings = useSelector(selectBackupSettings);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!open) setStep(1);
  }, [open]);

  const handleFinalConfirm = async () => {
    await dispatch(runBackupAndDelete());
    dispatch(resetEvents());
    dispatch(getEvents(1, true, null, 10));
    dispatch(fetchBackupStatus());
    dispatch(fetchSystemSettings());
    onClose();
  };

  const intervalDays = settings?.auto_backup_interval_days || 3;
  const untaggedCount = status?.untagged_count || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* ─── STEP 1 ─── */}
      {step === 1 && (
        <>
          <DialogTitle
            sx={{
              color: "#d32f2f",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningIcon />
            Backup AND Delete?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>This action will:</DialogContentText>
            <Box sx={{ mt: 2, pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                1. Upload media folder to Google Drive
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                2. Verify every file was uploaded
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: "#d32f2f" }}>
                3. <strong>Delete {untaggedCount} untagged events</strong> older
                than {intervalDays} days
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <strong>Featured events are protected.</strong>
              <br />
              Only events with no species tag will be deleted.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => setStep(2)}
              color="warning"
              variant="contained"
            >
              I Understand — Continue
            </Button>
          </DialogActions>
        </>
      )}

      {/* ─── STEP 2 ─── */}
      {step === 2 && (
        <>
          <DialogTitle
            sx={{
              color: "#d32f2f",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningIcon />
            FINAL CONFIRMATION
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>This action cannot be undone.</strong>
            </Alert>
            <DialogContentText>
              You are about to permanently delete{" "}
              <strong>{untaggedCount}</strong> untagged events after backing up.
            </DialogContentText>

            {backupRunning && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  Backing up + deleting... This may take 10-30 minutes.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep(1)} disabled={backupRunning}>
              Go Back
            </Button>
            <Button
              onClick={handleFinalConfirm}
              color="error"
              variant="contained"
              disabled={backupRunning}
              startIcon={<DeleteSweepIcon />}
            >
              {backupRunning ? "Working..." : `Yes, Backup & Delete`}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// AUTO-BACKUP SETTINGS DIALOG
// ═══════════════════════════════════════════════════════════

export function BackupSettingsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const settings = useSelector(selectBackupSettings);
  const status = useSelector(selectBackupStatus);
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(3);
  const [saving, setSaving] = useState(false);

  // Sync with Redux when dialog opens
  useEffect(() => {
    if (open && settings) {
      setEnabled(settings.auto_backup_enabled);
      setDays(settings.auto_backup_interval_days);
    }
  }, [open, settings]);

  useEffect(() => {
    if (open) {
      dispatch(fetchBackupStatus());
      dispatch(fetchSystemSettings());
    }
  }, [open, dispatch]);

  const handleSave = async () => {
    setSaving(true);
    await dispatch(
      updateSystemSettings({
        auto_backup_enabled: enabled,
        auto_backup_interval_days: days,
      }),
    );
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon color="primary" />
          Auto Backup Settings
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure automatic backup + cleanup. The server will run this
          automatically every N days.
        </Typography>

        {/* ─── Enable toggle ─── */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: enabled ? "#e8f5e9" : "#f5f5f5",
            borderRadius: 2,
            border: "1px solid",
            borderColor: enabled ? "#4caf50" : "#e0e0e0",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="success"
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Enable Auto Backup
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Server will backup + delete untagged events automatically
                </Typography>
              </Box>
            }
          />
        </Paper>

        {/* ─── Interval days ─── */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f5f5f5", borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Backup every N days
          </Typography>
          <TextField
            type="number"
            value={days}
            onChange={(e) =>
              setDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 3)))
            }
            inputProps={{ min: 1, max: 365 }}
            size="small"
            fullWidth
            helperText="Min: 1 day, Max: 365 days. Default: 3 days."
          />
        </Paper>

        {/* ─── Current status ─── */}
        {status && (
          <Paper sx={{ p: 2, bgcolor: "#fafafa", borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Current Status
            </Typography>

            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Last backup
                </Typography>
                <Typography variant="caption">
                  {settings?.last_backup_at
                    ? formatDistanceToNow(new Date(settings.last_backup_at), {
                        addSuffix: true,
                      })
                    : "Never"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={settings?.last_backup_status || "never"}
                  size="small"
                  color={
                    settings?.last_backup_status === "success"
                      ? "success"
                      : settings?.last_backup_status === "failed"
                        ? "error"
                        : "default"
                  }
                />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Untagged events
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {status.untagged_count}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Backed up events
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {status.backed_up_count}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Total events
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {status.total_events}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  Last cleanup count
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {settings?.last_cleanup_count || 0} deleted
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={<CheckCircleIcon />}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
